import { Inject, Injectable, InjectionToken, Optional, inject, signal } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { OmniChatCitation, OmniChatErrorEvent, OmniChatEvent, OmniChatTurn, consumeSseBody } from './kb-chat-sse.util';

/**
 * Gateway host for the CHAT-13 proxy, i.e. `environment.apiGatewayBaseURL`. Wired per-project in
 * `app.config.ts`, same pattern as `Wiki_BASE_URL`/`Wiki_ROUTE_PREFIX` in `wiki-help.service.ts`.
 */
export const KbChat_GATEWAY_BASE_URL = new InjectionToken<string>('KbChat_GATEWAY_BASE_URL');

/**
 * Local-dev override, i.e. `environment.chatLocalBaseURL`. Must default to `''` in every committed
 * `environment.*.ts` file — see the field's own doc comment in each environment file for why a
 * non-empty default there would be unsafe.
 */
export const KbChat_LOCAL_BASE_URL = new InjectionToken<string>('KbChat_LOCAL_BASE_URL');

export interface AskKbChatParams {
  question: string;
  history: OmniChatTurn[];
  signal?: AbortSignal;
}

export interface ReportKbChatParams {
  question: string;
  answer: string;
  citations?: OmniChatCitation[];
  pageUrl?: string;
  reason?: string;
  lang?: string;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Pre-stream failures (non-2xx) come back as plain JSON in principle, but the CHAT-13 proxy itself
 * (`KBChatController.cs`) returns bare status codes with no body for every pre-stream failure
 * (401/404/400/413/502/504) — so `res.json()` will throw there and this always falls back to the
 * generic `http_${status}` code. Both paths are handled: a body-carrying failure from some other
 * deployment of this endpoint still parses correctly.
 */
async function readPreStreamError(res: Response): Promise<OmniChatErrorEvent> {
  try {
    const json = (await res.json()) as Record<string, unknown>;
    return {
      type: 'error',
      code: asString(json['code']) ?? `http_${res.status}`,
      message: asString(json['message']) ?? `Request failed (${res.status}).`,
    };
  } catch {
    return {
      type: 'error',
      code: `http_${res.status}`,
      message: `Request failed (${res.status}).`,
    };
  }
}

/**
 * SSE client for the Ask AI chat backend.
 *
 * Deliberately bypasses `HttpClient`/`authInterceptor()`: SSE over POST needs raw `fetch()` +
 * `ReadableStream`, which the interceptor never sees. The bearer token is instead fetched fresh
 * immediately before each request via `OidcSecurityService.getAccessToken()` — the same pattern
 * commented out in `oxo.api.client.base.ts` — because the panel can sit open for a while before a
 * question is sent and tokens expire in the meantime.
 *
 * Endpoint resolution mirrors `WikiHelpService`: both DI tokens are `@Optional()`, so an app that
 * never wires them gets `isConfigured === false` and the "Ask AI" tab never renders (same rollout
 * mechanism as the related-articles panel).
 */
@Injectable({ providedIn: 'root' })
export class KbChatService {
  private readonly oidcSecurityService = inject(OidcSecurityService);

  /**
   * Whether the currently-loaded company is in the Ask AI rollout — set once by
   * `HeaderComponent` after `CompanyID` resolves from `getUserSystemListWithCompanyData`.
   * Shared here (rather than passed down as an `@Input`) so `AskPanelComponent` can read the same
   * value without new plumbing between the header and the panel.
   *
   * Starts `false` (fail closed): the panel can now open before the decision lands, because its
   * "Technical support" tab is shown to every company, so the Ask AI tabs must not show by default.
   */
  readonly companyInRollout = signal(false);

  constructor(
    @Optional() @Inject(KbChat_GATEWAY_BASE_URL) private readonly gatewayBaseUrl?: string,
    @Optional() @Inject(KbChat_LOCAL_BASE_URL) private readonly localBaseUrl?: string,
  ) {}

  get isConfigured(): boolean {
    return !!(this.gatewayBaseUrl || this.localBaseUrl);
  }

  /**
   * `chatLocalBaseURL` set → hit our own local backend directly (dev only, on
   * `http://localhost:8888` against the reference implementation's endpoint shape). Otherwise →
   * the CHAT-13 gateway proxy, which resolves scope and forwards to Athena server-side so the
   * caller's portal token never leaves the cluster.
   */
  private get askUrl(): string {
    if (this.localBaseUrl) {
      return `${this.localBaseUrl.replace(/\/+$/, '')}/public/v1/chat/ask`;
    }
    return `${(this.gatewayBaseUrl ?? '').replace(/\/+$/, '')}/settings/KBChat/Ask`;
  }

  /** Same two-path resolution as {@link askUrl}, pointed at the rollout-status endpoint. */
  private get rolloutStatusUrl(): string {
    if (this.localBaseUrl) {
      return `${this.localBaseUrl.replace(/\/+$/, '')}/public/v1/chat/rollout-status`;
    }
    return `${(this.gatewayBaseUrl ?? '').replace(/\/+$/, '')}/settings/KBChat/RolloutStatus`;
  }

  /**
   * Ask the backend whether Ask AI is enabled for the current company — the authoritative
   * rollout decision (subscription plan, evaluated server-side). `HeaderComponent` awaits this
   * after `CompanyID` resolves and fails closed (gate disabled) when this call fails — non-200
   * response, network error, or a body missing the `enabled` field all reject so the caller
   * knows not to trust a default.
   */
  async getRolloutStatus(): Promise<boolean> {
    const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());

    const res = await fetch(this.rolloutStatusUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      throw new Error(`RolloutStatus request failed (${res.status}).`);
    }

    const body = (await res.json()) as { enabled?: unknown };
    if (typeof body.enabled !== 'boolean') {
      throw new Error('RolloutStatus response missing boolean "enabled" field.');
    }
    return body.enabled;
  }

  /**
   * "Report a problem" endpoint. Mirrors `askUrl` exactly: local dev hits the llm-wiki backend's
   * `POST /public/v1/chat/report` directly, prod goes through the same CHAT-13 gateway proxy shape
   * as Ask.
   *
   * NOT YET LIVE IN PROD: `settings/KBChat/Report` has no forward route in OXO.APIs
   * (`KBChatController.cs`) as of this writing — only `.../Ask` exists there. That controller lives
   * in a separate repo and is out of scope for this change; wiring `Report` alongside the existing
   * `Ask` action (same pattern: resolve scope, forward to the llm-wiki `/public/v1/chat/report`
   * backend added for this feature) is the one remaining piece before this control works end to end
   * in prod. Building the URL the same way as `askUrl` now means no frontend change is needed once
   * that route exists.
   */
  private get reportUrl(): string {
    if (this.localBaseUrl) {
      return `${this.localBaseUrl.replace(/\/+$/, '')}/public/v1/chat/report`;
    }
    return `${(this.gatewayBaseUrl ?? '').replace(/\/+$/, '')}/settings/KBChat/Report`;
  }

  /**
   * Ask a question and stream back the response.
   *
   * Callers must pass an `AbortSignal` and abort it on unmount / a new question / panel close —
   * this generator does not cancel itself, and `fetch`/`reader.read()` reject with an `AbortError`
   * that propagates out of the loop for the caller to catch and swallow.
   */
  async *ask({ question, history, signal }: AskKbChatParams): AsyncGenerator<OmniChatEvent, void, unknown> {
    const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());

    const res = await fetch(this.askUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ question, history }),
      signal,
    });

    if (!res.ok) {
      yield await readPreStreamError(res);
      return;
    }
    if (!res.body) {
      yield { type: 'error', code: 'no_stream', message: 'The server did not return a stream.' };
      return;
    }

    yield* consumeSseBody(res.body);
  }

  /**
   * Flag one answer as wrong/unhelpful. Plain request/response (no SSE) — the backend just
   * persists the flagged turn for review. Same fresh-token-per-request pattern as `ask()`, for the
   * same reason (the panel can sit open long enough for a token to expire between messages).
   *
   * Throws on a non-2xx response so the caller can show a failure state; callers should not assume
   * this always succeeds.
   */
  async report(params: ReportKbChatParams): Promise<void> {
    const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());

    const res = await fetch(this.reportUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        question: params.question,
        answer: params.answer,
        citations: params.citations ?? [],
        pageUrl: params.pageUrl,
        reason: params.reason,
        lang: params.lang,
      }),
    });

    if (!res.ok) {
      const { message } = await readPreStreamError(res);
      throw new Error(message);
    }
  }
}
